import { Actions } from "./actions";
import { IO_ERROR, ErrorAction } from "../../../ajax/state/actions";
import { State } from "../state/state";
import { Commands, Mask } from "../../../shared";
import { fromServer, toServer } from "../filters";
import initial from "./state.initial";
import { Utils } from "../../../utils";
import { ReducerActions, createReducerActions } from "../../../Store/reducer";

type Actions =
  | Actions.GetRequest
  | Actions.GetResponse
  | Actions.EditRequest
  | Actions.EditResponse
  | Actions.AddRequest
  | Actions.AddResponse
  | Actions.DeleteRequest
  | Actions.DeleteResponse
  | Actions.EditAdd
  | Actions.EditAddCommands
  | Actions.EditTimers
  | Actions.EditTimersCommands
  | ErrorAction;

const actions: ReducerActions<State.Props, Actions> = {
  [Actions.REQUEST_GET]: getRequest,
  [Actions.RESPONSE_GET]: getResponse,
  [Actions.REQUEST_EDIT]: editRequest,
  [Actions.RESPONSE_EDIT]: editResponse,
  [Actions.RESPONSE_EDIT_END]: editResponseEnd,
  [Actions.REQUEST_ADD]: addRequest,
  [Actions.RESPONSE_ADD]: addResponse,
  [Actions.REQUEST_DELETE]: deleteRequest,
  [Actions.RESPONSE_DELETE]: deleteResponse,
  [Actions.EDIT_NEW]: editAdd,
  [Actions.EDIT_NEW_COMMANDS]: editAddCommands,
  [Actions.EDIT_TIMERS]: editTimers,
  [Actions.EDIT_TIMERS_COMMANDS]: editTimersCommands,
  [IO_ERROR]: error
};

export default createReducerActions(actions, initial);

/**
 * @brief Simple utility to compare timers
 *
 * @return
 */
const diffTimer = (a: State.Timer, b: State.Timer): Partial<State.Timer> =>
  Utils.diff(
    {
      label: a.label,
      startDate: a.startDate,
      startTime: a.startTime,
      interval: a.interval
    },
    {
      label: b.label,
      startDate: b.startDate,
      startTime: b.startTime,
      interval: b.interval
    }
  );

/**
 * @brief Simple utility to compare timer commands
 *
 * @return
 */
const diffTimerCmds = (a: State.Timer, b: State.Timer) => {
  let diff: Commands<Mask> = {};
  for (let key in a.cmds) {
    if (b.cmds[key]) {
      if (!(a.cmds[key].join("") == b.cmds[key].join(""))) {
        diff[key] = a.cmds[key];
      }
    }
  }
  return diff;
};

/**
 * @brief Action [REQUEST_GET] reducer
 */
function getRequest(state: State.Props): State.Props {
  return { ...state, loadingGet: true };
}

/**
 * @brief Action [RESPONSE_GET] Reducer
 */
function getResponse(
  state: State.Props,
  action: Actions.GetResponse
): State.Props {
  let timers: State.Timer[] = [];
  for (let key in action.data.schedule) {
    timers.push(fromServer(key, action.data.schedule[key]));
  }
  return {
    ...state,
    status: "OK",
    data: Object.assign({}, state.data, { timers: timers }),
    form: Object.assign({}, state.form, { timers: timers }),
    submitRequired: false,
    loadingGet: false
  };
}

/**
 * @brief Action [REQUEST_EDIT] Reducer
 */
function editRequest(state: State.Props, action: Actions.EditRequest) {
  action.post = {};
  // Loop timers look for changes to submit to server
  for (let i = 0; i < state.form.timers.length; i++) {
    // Diff timer primative properties
    let diff: Partial<State.Timer> = diffTimer(
      state.form.timers[i],
      state.data.timers[i]
    );

    // Diff timer command property
    let cmds: Commands<Mask> = diffTimerCmds(
      state.form.timers[i],
      state.data.timers[i]
    );

    // Action.post store diff per timer and needs submit
    if (Object.keys(cmds).length) diff.cmds = cmds;
    if (Object.keys(diff).length) {
      // TODO - Server does not accept partial edit. So we send entire form
      // object instead of toServer(diff). Should server accept partial then we
      // can send the diff
      // action.post[`${state.form.timers[i].label}`] = toServer(diff);
      action.post[`${state.form.timers[i].label}`] = toServer(
        state.form.timers[i]
      );
    }
  }
  return { ...state, loadingPost: true };
}

/**
 * @brief Action [RESPONSE_EDIT] Reducer
 */
function editResponse(state: State.Props): State.Props {
  return { ...state };
}

/**
 * @breif Action [RESPONSE_EDIT_END] Reducer
 */
function editResponseEnd(state: State.Props): State.Props {
  return { ...state, loadingPost: false };
}

/**
 * @brief
 */
function addRequest(
  state: State.Props,
  action: Actions.AddRequest
): State.Props {
  action.post = {};
  action.post[`${action.data.label}`] = toServer(action.data);
  return { ...state, loadingPost: true };
}

/**
 * @brief
 */
function addResponse(state: State.Props): State.Props {
  return { ...state, loadingPost: false };
}

/**
 * @brief
 */
function deleteRequest(state: State.Props): State.Props {
  return { ...state, loadingPost: true };
}

/**
 * @brief
 */
function deleteResponse(state: State.Props): State.Props {
  return { ...state, loadingPost: false };
}

/**
 * @brief
 */
function editAdd(state: State.Props, action: Actions.EditAdd): State.Props {
  const edit = Object.assign({}, state.form.newTimer, action.data);
  const form = Object.assign({}, state.form, { newTimer: edit });
  const submitRequired = Object.keys(diffTimer(edit, state.data.newTimer))
    .length
    ? true
    : false;
  return { ...state, form, submitRequired };
}
/**
 * @brief
 *
 * @return
 */
function editAddCommands(
  state: State.Props,
  action: Actions.EditAddCommands
): State.Props {
  const { url, output, cmd } = action;
  const oldCmds = state.form.newTimer.cmds;
  const editCmds = Object.assign({}, oldCmds, {
    [url]: Object.assign([], oldCmds[url], { [output]: cmd })
  });
  const edit = Object.assign({}, state.form.newTimer, { cmds: editCmds });
  const form = Object.assign({}, state.form, { newTimer: edit });
  const submitRequired = Object.keys(diffTimerCmds(edit, state.data.newTimer))
    .length
    ? true
    : false;
  return {
    ...state,
    form,
    submitRequired
  };
}

/**
 * @brief Action [EDIT] Reducer
 */
function editTimers(
  state: State.Props,
  action: Actions.EditTimers
): State.Props {
  const timers: State.Timer[] = Object.assign([], state.form.timers, {
    [action.idx]: Object.assign({}, state.form.timers[action.idx], action.data)
  });
  const form: State.Data = Object.assign({}, state.form, { timers: timers });
  let submitRequired = false;
  for (let i in form.timers) {
    if (Object.keys(diffTimer(form.timers[i], state.data.timers[i])).length) {
      submitRequired = true;
    }
  }
  return { ...state, ...{ form }, submitRequired };
}

/**
 * @brief Merge new mask property into existing mask property for the selected
 * timer
 */
function editTimersCommands(
  state: State.Props,
  action: Actions.EditTimersCommands
): State.Props {
  const { idx, url, output, cmd } = action;
  const oldCmds = state.form.timers[idx].cmds;
  const editCmds = Object.assign({}, oldCmds, {
    [url]: Object.assign([], oldCmds[url], { [output]: cmd })
  });
  const edit = Object.assign({}, state.form.timers[idx], { cmds: editCmds });
  const timers: State.Timer[] = Object.assign([], state.form.timers, {
    [action.idx]: edit
  });
  const form = Object.assign({}, state.form, { timers: timers });
  const submitRequired = Object.keys(
    diffTimerCmds(edit, state.data.timers[idx])
  ).length
    ? true
    : false;
  return {
    ...state,
    form,
    submitRequired
  };
}

function error(state: State.Props): State.Props {
  return { ...state, loadingPost: false, loadingGet: false };
}
